import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSetModuleFavorite, useSetModuleUnfavorite } from "../../shared/hooks/useModules";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import { IModuleProps } from "./Module.interface";
import { Outlet, useOutlet, useParams } from "react-router-dom";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import { ModuleContext } from "../../shared/providers/module/module.provider";
import FavoriteButton from "../../components/FavoriteButton/FavoriteButton";
import Placeholder from "./../../shared/assets/imgs/placeholder.svg";
import Info from "./../../shared/assets/imgs/info.svg";
import {
    getDeepLink,
    getFormattedPath,
    handleFileDownload,
    markdownToHtml
} from "../../shared/helpers/utils";
import Link from "../../components/Link/Link";
import UnitCard from "../../components/UnitCard/UnitCard";
import { CONTENT_BASE_URL, MODULE_TYPES } from "../../shared/constants";
import { AnalyticsContext } from "../../shared/providers/analytics/analytics.provider";
import { UserContext } from "../../shared/providers/user/user.provider";
import { routes } from "../../shared/router/routes";
import ModuleCard from "../../components/ModuleCard/ModuleCard";
import Button from "../../components/Button/Button";
import { IModule } from "../../shared/interfaces/modules.interface";

function Module(props: IModuleProps) {
    const outlet = useOutlet();
    const { translate } = useContext(TranslationContext);
    const { setEvent } = useContext(AnalyticsContext);
    const { user, userSettings } = useContext(UserContext);
    const { setIsLoading } = useContext(LoadingContext);
    const { pathwayModules, module, setUpdated, setCurrentModuleId, setModule } =
        useContext(ModuleContext);
    const { moduleId: id } = useParams();

    const setFavorite = useSetModuleFavorite();
    const setUnfavorite = useSetModuleUnfavorite();
    const hasDocuments = useMemo(
        () => module?.documents && module?.documents?.length > 0,
        [module]
    );
    
    const suggestions = useMemo(
        () => (module?.related_modules?.reduce((list: IModule[], id: string) => {
            const module = pathwayModules?.find(mod => mod.id === id);

            if (!module) return list;

            return [...list, module]
        }, [])) ?? []
        ,
        [pathwayModules, module]
    );

    const canDefineGoals = useMemo(() => userSettings?.myActivityGoalsCanDefine, [userSettings])

    const completedUnits = useMemo(() => {
        return (
            module?.unitsCompleted ?? 0
        );
    }, [module]);

    const nextUnitId = useMemo(() => {
        const [nextUnit] =
            module?.units?.filter((unit) => !unit.isCompleted) ?? module?.units ?? [];

        return nextUnit?.id ?? module?.units?.[0]?.id ?? false;
    }, [module]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [module]);

    useEffect(() => {
        setCurrentModuleId(id);
        return () => {
            setCurrentModuleId(undefined);
            setModule(undefined);
        };
    }, [id]);

    const handleFavorite = async () => {
        if (!id || !module) return;
        setIsLoading(true);

        if (!module.isFavorite) await setFavorite.mutateAsync(id);
        else await setUnfavorite.mutateAsync(id);

        setEvent({
            event_name: "app_favourites",
            event_category: "content_navigation",
            event_action: "app_favourites",
            user_token: user?.id,
            company_id: user?.company.id,
            module_id: module.id
        });

        setUpdated(new Date());
        setIsLoading(false);
    };

    const navigateToExplore = useCallback(() => {
        setEvent({
            event_name: "app_module_explore",
            event_category: "content_navigation",
            event_action: "app_module_explore",
            user_token: user?.id,
            company_id: user?.company.id,
            module_id: id
        });

        window.location.href = getDeepLink("app/explore_screen");
    }, [id]);

    useEffect(()=>{
        setIsLoading(!module);
    },[module])

    return (
        <>
            {outlet && <Outlet />}
            {!outlet && module && (
                <div className="h-100 d-flex flex-column pt-2 pb-6">
                    <div className="container-fluid gx-2 mb-2">
                        <section className="module row g-1">
                            <div className="col-12 d-flex flex-column gap-02">
                                <div className="d-flex justify-content-between">
                                    <span
                                        className={`color-${module?.tag_color ?? "yellow"}-500 text-bd4-bd text-uppercase`}>
                                        {module?.tag}
                                    </span>
                                    <FavoriteButton
                                        className="ms-auto"
                                        isFavorite={module?.isFavorite}
                                        onClick={handleFavorite}
                                    />
                                </div>
                                <h1 className="text-h1-bd">{module?.title}</h1>
                                <div className="d-flex justify-content-between">
                                    <span className="d-flex gap-01 align-items-center color-neutrals-dark text-bd3-rg">
                                        <i className={`icon icon-16 icon-Time`}></i>
                                        {module?.units?.reduce((unitAcc, unit) => {
                                            return (unitAcc += unit.items.reduce(
                                                (itemAcc, item) => {
                                                    return (itemAcc +=
                                                        !item || !item.duration
                                                            ? 0
                                                            : item.duration);
                                                },
                                                0
                                            ));
                                        }, 0)}
                                        min {translate("common.article", "article")}
                                    </span>
                                    {module?.units && (
                                        <span className="color-neutrals-dark text-bd3-rg">
                                            {`${!module?.unitsProgress ? 0 : completedUnits} / ${module?.units?.length} ${module?.units?.length > 1
                                                ? translate("common.units", "units")
                                                : translate("common.unit", "unit")
                                                }`}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <figure className="col-12">
                                <img
                                    className="module_thumbnail"
                                    src={
                                        module?.image?.url
                                            ? `${CONTENT_BASE_URL}${module?.image?.url}`
                                            : Placeholder
                                    }
                                    alt={module?.image?.alt ?? module?.title}
                                />
                            </figure>
                            {module?.description && <div className="col-12">
                                <span
                                    className="module_description color-neutrals-black text-bd2-rg rich_text"
                                    dangerouslySetInnerHTML={{
                                        __html: markdownToHtml(module.description)
                                    }}
                                />
                            </div>}
                            <div className="col-12 d-flex flex-column gap-2">
                                <ul className="d-flex flex-column gap-02">
                                    {module?.units?.map((unit, i) => (
                                        <li key={i}>
                                            <UnitCard
                                                progress={module.unitsProgress?.find(
                                                    (u) => u.id === unit.id
                                                )}
                                                unit={unit}
                                                index={i + 1}
                                                moduleId={module.id}
                                                moduleImage={
                                                    module.image?.url
                                                        ? `${CONTENT_BASE_URL}${module.image?.url}`
                                                        : Placeholder
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                                <Link
                                    variant="solid"
                                    customCss="w-100"
                                    to={getFormattedPath(routes.UNIT.path, {
                                        moduleId: module?.id,
                                        unitId: nextUnitId
                                    })}>
                                    {completedUnits > 0
                                        ? translate("module.continue_module", "Continue module")
                                        : translate("module.start_module", "Start module")}
                                </Link>
                            </div>
                        </section>
                    </div>

                    {hasDocuments && (
                        <section className="bg-blue-50 py-2" aria-labelledby="documents_title">
                            <div className="container bg-blue-50 gx-2">
                                <div className="row">
                                    <div className="col-12">
                                        <h2 id="documents_title" className="text-h5-smbd mb-12">
                                            {translate(
                                                "modules.support_materials",
                                                "Support Materials"
                                            )}
                                        </h2>
                                        <div className="card card_documents bg-neutrals-white d-flex flex-column gap-1 p-1">
                                            <h3 className="text-h5-smbd">
                                                {translate("common.documents", "Documents")}
                                            </h3>
                                            <ul>
                                                {module?.documents?.map((doc, i) => (
                                                    <li key={i} className="card_document d-flex">
                                                        <div className="d-flex flex-wrap gap-02 w-100 py-03">
                                                            <Button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleFileDownload(
                                                                        `${CONTENT_BASE_URL}${doc.url}`,
                                                                        doc.alt ?? doc.name
                                                                    )
                                                                }
                                                                customCss="justify-content-start button card_title text-h5-smbd w-100 p-0  ">
                                                                {doc.alt ?? doc.name}
                                                            </Button>
                                                            <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                                {doc.ext?.replace(".", "")}
                                                            </span>
                                                            <span className="color-neutrals-neutral text-bd3-rg text-uppercase">
                                                                {Math.round(doc.size)}KB
                                                            </span>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            icon="Download"
                                                            iconSize={24}
                                                            onClick={() =>
                                                                handleFileDownload(
                                                                    `${CONTENT_BASE_URL}${doc.url}`,
                                                                    doc.alt ?? doc.name
                                                                )
                                                            }
                                                            customCss="justify-content-start button card_title text-h5-smbd p-0"></Button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {module?.type === MODULE_TYPES.PSYCHOEDUCATIONAL &&
                        canDefineGoals &&
                        !!module.moduleProgress &&
                        module.moduleProgress >= 100 && (
                            <div className="container bg-neutrals-white gx-2">
                                <div className="row">
                                    <div className="col-12 py-2 d-flex flex-column gap-12">
                                        <img src={Info} width={83} height={53} />
                                        <div className="d-flex flex-column gap-02">
                                            <h2 className="text-h5-smbd">
                                                {translate(
                                                    "module.activity_goals_unlocked_title",
                                                    "Well done on completing your module!"
                                                )}
                                            </h2>
                                            <p className="text-bd2-rg color-neutrals-neutral my-0">
                                                {translate(
                                                    "module.activity_goals_unlocked_description",
                                                    "You can now set up your goals and start keeping track of your progress."
                                                )}
                                            </p>
                                        </div>
                                        <Link
                                            to={getDeepLink(
                                                "app/wellness_center_screen?activityGoalUnlocked=true"
                                            )}
                                            variant="solid">
                                            {translate(
                                                "module.activity_goals_unlocked_button",
                                                "Define your goals now"
                                            )}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        )}

                    {suggestions && suggestions?.length > 0 && (
                        <section aria-labelledby="suggestions_title" className="bg-blue-50 pt-2 pb-6">
                            <div className="container gx-2">
                                <div className="row">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-between mb-1">
                                            <h2 className="text-h4-md" id="suggestions_title">
                                                {translate("common.suggestions", "Suggestions")}
                                            </h2>
                                            <Button
                                                type="button"
                                                onClick={navigateToExplore}
                                                customCss="color-green-600"
                                                label={translate("common.view_all", "View all")}
                                                icon="Chevron--right"
                                                isIconRight
                                                variant="link" />
                                        </div>
                                        <div className="d-flex flex-column gap-02">
                                            {suggestions?.map((mod, i) => (
                                                <ModuleCard key={i} {...mod} isSmall fromModule />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            )}
        </>
    );
}

export default Module;
